/**
 * InboxChatScreen — realtime DM chat with image support.
 * Uses Supabase Broadcast for instant delivery + 30s fallback poll.
 * Premium minimalist dark design. Text + up to 3 images per message.
 */

import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Send, ChevronDown, Pencil } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { format, isToday, isYesterday } from 'date-fns'
import { motion, AnimatePresence } from 'framer-motion'
import { useQueryClient } from '@tanstack/react-query'
import { useRealtimeChat } from '@turnifly/shared/hooks'
import { useMarkRead, messagingKeys } from '@turnifly/shared/hooks'
import { useAuthStore } from '@turnifly/shared/stores'
import { useMediaUpload } from '@turnifly/shared/hooks'
import { LoadingSpinner } from '@turnifly/shared/ui'
import { AvatarWithBadge } from '@turnifly/shared/ui'
import { ChatBubble } from './chat-bubble'
import { ChatMediaInput, ChatMediaPreview, ChatFileInput } from './chat-media-input'
import { ChatImageViewer } from './chat-image-viewer'
import { ChatMessageMenu } from './chat-message-menu'
import type { MessageMenuState } from './chat-message-menu'
import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'
import { supabase } from '@turnifly/shared/api'
import { updateMessageText } from '@turnifly/shared/api'
import type { MessageItem } from '@turnifly/shared/api'

// ── Types ──────────────────────────────────────────────────

interface DateGroup { label: string; messages: MessageItem[] }
interface LocationState { participantName?: string; avatarUrl?: string | null }
interface LightboxState { images: string[]; index: number }

const MAX_IMAGES = 3

// ── Component ──────────────────────────────────────────────

export const InboxChatScreen: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [inputText, setInputText] = useState('')
  const [mediaUrls, setMediaUrls] = useState<string[]>([])
  const [lightbox, setLightbox] = useState<LightboxState | null>(null)
  const [menuState, setMenuState] = useState<MessageMenuState | null>(null)
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null)

  const state = location.state as LocationState | null
  const participantName = state?.participantName ?? t('dm.chat')
  const avatarUrl = state?.avatarUrl ?? null

  // ── Hooks ───────────────────────────────────────────────

  const {
    messages, isLoading, isConnected, isSending,
    hasNewMessages, sendMessage, dismissNewMessages,
  } = useRealtimeChat(conversationId, user?.id)

  const queryClient = useQueryClient()
  const markRead = useMarkRead(conversationId ?? '', user?.id ?? '')
  const { upload, isUploading } = useMediaUpload()

  // Mark as read on open
  useEffect(() => {
    if (conversationId && user?.id) markRead.mutate()
  }, [conversationId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Smart auto-scroll ───────────────────────────────────

  const isAtBottomRef = useRef(true)

  const handleScroll = useCallback((): void => {
    if (!scrollRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
    const atBottom = scrollHeight - scrollTop - clientHeight < 100
    isAtBottomRef.current = atBottom
    // Notify realtime hook
    window.dispatchEvent(new CustomEvent('chat-scroll-bottom', { detail: atBottom }))
  }, [])

  useEffect(() => {
    if (isAtBottomRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages.length])

  const scrollToBottom = useCallback((): void => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
      dismissNewMessages()
    }
  }, [dismissNewMessages])

  // ── Date groups ─────────────────────────────────────────

  const dateGroups = useMemo((): DateGroup[] => {
    if (!messages.length) return []
    const groups: DateGroup[] = []
    let currentDate = ''
    for (const msg of messages) {
      const msgDate = new Date(msg.created_at)
      const dateKey = format(msgDate, 'yyyy-MM-dd')
      if (dateKey !== currentDate) {
        currentDate = dateKey
        const label = isToday(msgDate) ? t('dm.today')
          : isYesterday(msgDate) ? t('dm.yesterday')
          : format(msgDate, 'dd.MM.yyyy')
        groups.push({ label, messages: [] })
      }
      groups[groups.length - 1].messages.push(msg)
    }
    return groups
  }, [messages, t])

  // ── Send ────────────────────────────────────────────────

  const handleSend = useCallback((): void => {
    const hasText = inputText.trim().length > 0
    const hasMedia = mediaUrls.length > 0

    // Edit mode: update existing message text
    if (editingMessageId && hasText) {
      hapticImpact('light')
      void updateMessageText(editingMessageId, inputText.trim()).then(() => {
        void queryClient.invalidateQueries({ queryKey: messagingKeys.messages(conversationId ?? '') })
      })
      setEditingMessageId(null)
      setInputText('')
      return
    }

    if ((!hasText && !hasMedia) || !user?.id) return

    hapticImpact('light')
    sendMessage(hasText ? inputText.trim() : null, mediaUrls.length > 0 ? mediaUrls : undefined)
    setInputText('')
    setMediaUrls([])
    inputRef.current?.focus()

    // Auto-scroll on send
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }, 50)
  }, [inputText, mediaUrls, user?.id, sendMessage, editingMessageId, conversationId, queryClient])

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  // ── Image upload ────────────────────────────────────────

  const handleFilesSelected = useCallback(async (files: FileList): Promise<void> => {
    const remaining = MAX_IMAGES - mediaUrls.length
    const toUpload = Array.from(files).slice(0, remaining)
    for (const file of toUpload) {
      try {
        const result = await upload(file, 'dm')
        if (result?.url) setMediaUrls((prev) => [...prev, result.url])
      } catch { /* skip failed */ }
    }
  }, [mediaUrls.length, upload])

  const handleRemoveImage = useCallback((index: number): void => {
    setMediaUrls((prev) => prev.filter((_, i) => i !== index))
  }, [])

  // ── Lightbox ────────────────────────────────────────────

  const handleImageTap = useCallback((images: string[], index: number): void => {
    setLightbox({ images, index })
  }, [])

  // ── Long press menu ─────────────────────────────────────

  const handleLongPress = useCallback((messageId: string, text: string | null, isMine: boolean): void => {
    hapticImpact('medium')
    setMenuState({ messageId, text, isMine })
  }, [])

  const handleCopyText = useCallback((text: string): void => {
    void navigator.clipboard.writeText(text)
  }, [])

  const handleEditMessage = useCallback((messageId: string, currentText: string): void => {
    setEditingMessageId(messageId)
    setInputText(currentText)
    inputRef.current?.focus()
  }, [])

  const handleCancelEdit = useCallback((): void => {
    setEditingMessageId(null)
    setInputText('')
  }, [])

  const handleDeleteMessage = useCallback(async (messageId: string): Promise<void> => {
    if (!conversationId) return
    try {
      await supabase.from('messages').delete().eq('id', messageId).throwOnError()
      void queryClient.invalidateQueries({ queryKey: messagingKeys.messages(conversationId) })
      void queryClient.invalidateQueries({ queryKey: ['messaging', 'conversations'] })
    } catch { /* ignore */ }
  }, [conversationId, queryClient])

  // ── Auto-resize textarea ────────────────────────────────

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setInputText(e.target.value)
    const el = e.target
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`
  }

  const canSend = (inputText.trim().length > 0 || mediaUrls.length > 0) && !isUploading

  return (
    <div className="h-[100dvh] flex flex-col bg-bg">
      {/* ── Header ── */}
      <div
        className="flex items-center gap-3 px-3 border-b border-white/[0.04] shrink-0"
        style={{
          paddingTop: 'calc(var(--tg-content-safe-area-inset-top, 0px) + var(--tg-safe-area-inset-top, 0px) + 8px)',
          paddingBottom: '12px',
        }}
      >
        <button onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-full
            hover:bg-white/[0.06] transition-colors tap-highlight-transparent">
          <ArrowLeft size={18} className="text-text-hint" />
        </button>
        <AvatarWithBadge src={avatarUrl} fallback={participantName.charAt(0).toUpperCase()} size="sm" />
        <div className="flex-1 min-w-0">
          <h2 className="text-[15px] font-semibold text-text truncate leading-tight">{participantName}</h2>
          {isConnected && (
            <p className="text-[10px] text-success/60 leading-tight">{t('dm.connected')}</p>
          )}
        </div>
      </div>

      {/* ── Messages Area ── */}
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto overscroll-y-contain">
        <div className="max-w-lg mx-auto px-3 py-3">
          {isLoading ? (
            <div className="flex justify-center py-16"><LoadingSpinner size="md" /></div>
          ) : !messages.length ? (
            <EmptyChat participantName={participantName} />
          ) : (
            dateGroups.map((group) => (
              <div key={group.label}>
                <div className="flex items-center justify-center py-3">
                  <span className="px-3 py-1 rounded-full bg-white/[0.04] text-[11px] text-text-hint/60 font-medium">
                    {group.label}
                  </span>
                </div>
                <div className="space-y-0.5">
                  {group.messages.map((msg, idx) => {
                    const isMine = msg.sender_id === user?.id
                    const prev = group.messages[idx - 1]
                    const isFirstInGroup = !prev || prev.sender_id !== msg.sender_id
                      || new Date(msg.created_at).getTime() - new Date(prev.created_at).getTime() > 60_000
                    return (
                      <ChatBubble
                        key={msg.id}
                        id={msg.id}
                        text={msg.text}
                        mediaUrls={Array.isArray(msg.media_urls) ? msg.media_urls : []}
                        time={msg.created_at}
                        editedAt={msg.edited_at ?? null}
                        isMine={isMine}
                        isFirstInGroup={isFirstInGroup}
                        onImageTap={handleImageTap}
                        onLongPress={handleLongPress}
                      />
                    )
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── New messages indicator ── */}
      <AnimatePresence>
        {hasNewMessages && (
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            onClick={scrollToBottom}
            className="absolute bottom-24 left-1/2 -translate-x-1/2 z-10
              px-4 py-1.5 rounded-full bg-primary/90 backdrop-blur-sm
              flex items-center gap-1.5 shadow-lg tap-highlight-transparent"
          >
            <ChevronDown size={14} className="text-white" />
            <span className="text-xs font-medium text-white">{t('dm.newMessages')}</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* ── Edit indicator ── */}
      {editingMessageId && (
        <div className="flex items-center gap-2 px-4 py-2 border-t border-white/[0.04] bg-primary/[0.05]">
          <Pencil size={14} className="text-primary shrink-0" />
          <span className="text-xs text-primary flex-1 truncate">{t('dm.editing')}</span>
          <button onClick={handleCancelEdit} className="text-xs text-text-hint tap-highlight-transparent">
            {t('dm.cancel')}
          </button>
        </div>
      )}

      {/* ── Media preview strip ── */}
      <ChatMediaPreview images={mediaUrls} isUploading={isUploading} onRemoveImage={handleRemoveImage} />

      {/* ── Input Area ── */}
      <div
        className="px-3 pt-2 shrink-0 border-t border-white/[0.04]"
        style={{ paddingBottom: 'max(12px, calc(var(--tg-safe-area-inset-bottom) + 6px))' }}
      >
        <div className="flex items-end gap-1.5 max-w-lg mx-auto">
          <ChatMediaInput
            images={mediaUrls}
            isUploading={isUploading}
            onPickFiles={() => fileInputRef.current?.click()}
            disabled={isSending}
          />
          <textarea
            ref={inputRef}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={t('dm.messagePlaceholder')}
            maxLength={2000}
            rows={1}
            className="flex-1 px-4 py-2.5 rounded-[20px] bg-white/[0.04] border border-white/[0.06]
              text-text text-[14px] placeholder:text-text-hint/40 resize-none leading-[1.4]
              focus:outline-none focus:border-white/[0.12] transition-colors"
            style={{ maxHeight: '96px' }}
          />
          <motion.button
            onClick={handleSend}
            disabled={!canSend || isSending}
            whileTap={{ scale: 0.9 }}
            className="w-9 h-9 flex items-center justify-center rounded-full shrink-0
              bg-primary text-white transition-all
              disabled:bg-white/[0.04] disabled:text-text-hint/30"
          >
            <Send size={15} />
          </motion.button>
        </div>
      </div>

      {/* Hidden file input */}
      <ChatFileInput inputRef={fileInputRef} onFilesSelected={handleFilesSelected} />

      {/* Image lightbox */}
      {lightbox && (
        <ChatImageViewer
          images={lightbox.images}
          initialIndex={lightbox.index}
          isOpen
          onClose={() => setLightbox(null)}
        />
      )}

      {/* Long-press context menu */}
      <ChatMessageMenu
        state={menuState}
        onClose={() => setMenuState(null)}
        onCopy={handleCopyText}
        onEdit={handleEditMessage}
        onDelete={handleDeleteMessage}
      />
    </div>
  )
}

// ── Empty State ────────────────────────────────────────────

const EmptyChat: React.FC<{ participantName: string }> = ({ participantName }) => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="w-14 h-14 rounded-full bg-white/[0.04] flex items-center justify-center mb-4">
        <span className="text-2xl font-display font-bold text-text-hint/30">
          {participantName.charAt(0).toUpperCase()}
        </span>
      </div>
      <p className="text-sm text-text-hint/50">{t('dm.emptyChat')}</p>
    </div>
  )
}
